import * as missionApi from '@/api/mission'
import { getToken } from '@/utils/auth'

const state = {
  nodes: [],
  messages: [],
  events: [],
  wsConnected: false,
  liveEvents: [],
}

const getters = {
  nodes: (s) => s.nodes,
  onlineNodes: (s) => s.nodes.filter((n) => n.online),
  messages: (s) => s.messages,
  events: (s) => s.events,
  wsConnected: (s) => s.wsConnected,
  liveEvents: (s) => s.liveEvents,
  nodeById: (s) => (id) => s.nodes.find((n) => n.id === id),
}

let ws = null
let reconnectTimer = null
const webrtcListeners = new Set()

const actions = {
  async fetchNodes({ commit }) {
    const { data } = await missionApi.listNodes()
    commit('SET_NODES', data.data.nodes || [])
  },

  async updateNode({ dispatch }, { id, name }) {
    const { data } = await missionApi.updateNode(id, { name })
    await dispatch('fetchNodes')
    return data.data.node
  },

  async deleteNode({ dispatch }, id) {
    await missionApi.deleteNode(id)
    await dispatch('fetchNodes')
  },

  async resetNode({ dispatch }, id) {
    await missionApi.resetNode(id)
    await dispatch('fetchNodes')
  },

  async generatePairingCode() {
    const { data } = await missionApi.generatePairingCode()
    return data.data
  },

  async generatePairingToken() {
    const { data } = await missionApi.generatePairingToken()
    return data.data
  },

  async sendCommand(_, payload) {
    const { data } = await missionApi.sendCommand(payload)
    return data.data
  },

  async fetchMessages({ commit }) {
    const { data } = await missionApi.listMessages()
    commit('SET_MESSAGES', data.data.messages || [])
  },

  async sendMessage({ dispatch }, payload) {
    await missionApi.createMessage(payload)
    await dispatch('fetchMessages')
  },

  async sendAlert(_, payload) {
    const { data } = await missionApi.sendAlert(payload)
    return data.data
  },

  async fetchEvents({ commit }, nodeId) {
    const { data } = await missionApi.listEvents(nodeId)
    commit('SET_EVENTS', data.data.events || [])
  },

  connectWS({ commit, dispatch }) {
    if (ws) return
    const token = getToken()
    const url = `${missionApi.getDashboardWSUrl()}?token=${encodeURIComponent(token)}`
    ws = new WebSocket(url)

    ws.onopen = () => {
      commit('SET_WS_CONNECTED', true)
    }

    ws.onclose = () => {
      commit('SET_WS_CONNECTED', false)
      ws = null
      reconnectTimer = setTimeout(() => dispatch('connectWS'), 3000)
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'webrtc') {
          webrtcListeners.forEach((fn) => {
            try { fn(msg) } catch (e) { /* ignore */ }
          })
        }
        commit('ADD_LIVE_EVENT', msg)
        if (msg.action === 'ready_status') {
          let payload = msg.payload
          if (typeof payload === 'string') {
            try { payload = JSON.parse(payload) } catch (e) { payload = null }
          }
          if (msg.nodeId && payload && typeof payload.ready === 'boolean') {
            commit('PATCH_NODE', { id: msg.nodeId, ready_status: payload.ready })
          }
        } else if (msg.action === 'system_stats') {
          let payload = msg.payload
          if (typeof payload === 'string') {
            try { payload = JSON.parse(payload) } catch (e) { payload = null }
          }
          if (msg.nodeId && payload) {
            commit('PATCH_NODE', { id: msg.nodeId, system_stats: payload })
          }
        }
      } catch (e) {
        // ignore
      }
    }
  },

  disconnectWS({ commit }) {
    if (reconnectTimer) clearTimeout(reconnectTimer)
    if (ws) {
      ws.close()
      ws = null
    }
    commit('SET_WS_CONNECTED', false)
  },

  sendWSCommand(_, { nodeId, adapter, action, payload }) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({
      type: 'command',
      nodeId,
      adapter,
      action,
      payload: payload || {},
      correlationId: `${Date.now()}`,
    }))
  },

  sendWebRTCSignal(_, { nodeId, sessionId, action, payload }) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({
      type: 'webrtc',
      nodeId,
      correlationId: sessionId,
      action,
      payload: payload || {},
    }))
  },

  registerWebRTCListener(_, fn) {
    if (typeof fn === 'function') webrtcListeners.add(fn)
  },

  unregisterWebRTCListener(_, fn) {
    webrtcListeners.delete(fn)
  },
}

const mutations = {
  SET_NODES(state, nodes) {
    state.nodes = nodes
  },
  PATCH_NODE(state, { id, ...fields }) {
    const i = state.nodes.findIndex((n) => n.id === id)
    if (i === -1) return
    state.nodes.splice(i, 1, { ...state.nodes[i], ...fields })
  },
  SET_MESSAGES(state, messages) {
    state.messages = messages
  },
  SET_EVENTS(state, events) {
    state.events = events
  },
  SET_WS_CONNECTED(state, val) {
    state.wsConnected = val
  },
  ADD_LIVE_EVENT(state, event) {
    state.liveEvents.unshift(event)
    if (state.liveEvents.length > 100) state.liveEvents.pop()
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
